import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import { CATEGORY_SETS } from "../../../data/videos/workoutVideos";

function BarbellIcon({ className = "w-8 h-8" }) {
    return (
        <svg viewBox="0 0 64 64" className={className} aria-hidden>
            <rect x="6" y="26" width="10" height="12" rx="2" fill="currentColor" />
            <rect x="48" y="26" width="10" height="12" rx="2" fill="currentColor" />
            <rect x="20" y="29" width="24" height="6" rx="2" fill="currentColor" />
        </svg>
    );
}

export default function WorkoutPrescriptionVideo() {
    const ready = useLoadingGate();
    const navigate = useNavigate();

    // 드롭다운
    const [open, setOpen] = useState(false);
    const [catKey, setCatKey] = useState(CATEGORY_SETS[0]?.key ?? "tool");
    const selectRef = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (!selectRef.current) return;
            if (!selectRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const current = useMemo(
        () => CATEGORY_SETS.find((c) => c.key === catKey) ?? CATEGORY_SETS[0],
        [catKey]
    );

    const handlePick = (k) => {
        setCatKey(k);
        setOpen(false);
    };

    // 여기서 name 대신 item.key(슬러그)를 넘긴다
    const handleItemClick = (itemKey) => {
        navigate(
            `/workout/workoutguide/video?cat=${encodeURIComponent(
                catKey
            )}&item=${encodeURIComponent(itemKey)}`
        );
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title={"피트니스 운동"}
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-3 md:pt-4 pb-24 md:pb-28">
                        {/* 드롭다운 */}
                        <div className="relative" ref={selectRef}>
                            <button
                                type="button"
                                onClick={() => setOpen((v) => !v)}
                                className="w-full h-12 md:h-14 px-4 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 flex items-center justify-between shadow-sm"
                                aria-haspopup="listbox"
                                aria-expanded={open}
                            >
                                <span className="font-medium">{current.label}</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-700 transition-transform ${open ? "rotate-180" : ""
                                        }`}
                                />
                            </button>

                            {open && (
                                <ul
                                    role="listbox"
                                    className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
                                >
                                    {CATEGORY_SETS.map((c) => (
                                        <li key={c.key} role="option" aria-selected={catKey === c.key}>
                                            <button
                                                className={`w-full text-left h-11 px-4 ${catKey === c.key
                                                    ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold"
                                                    : "hover:bg-gray-100"
                                                    }`}
                                                onClick={() => handlePick(c.key)}
                                            >
                                                {c.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* 구분선 */}
                        <div className="my-3 md:my-4">
                            <div className="h-px bg-gray-100" />
                        </div>

                        <section className="grid grid-cols-2 gap-3 md:gap-4">
                            {current.items.map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => handleItemClick(item.key)}
                                    className="h-36 md:h-44 rounded-2xl border border-gray-200 bg-white shadow-sm flex items-center justify-center active:scale-[.99] transition overflow-hidden"
                                >
                                    <img
                                        src={item.image}
                                        alt={item.label}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </section>
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
